import React, { useEffect, useRef, useState } from "react";
import UserAvatar from "../../common/UserAvatar/UserAvatar";
import { uploadProfilePhoto } from "../../../api/profilePhotos";
import { executeQuery, executeMutation } from "../../../graphql/client";
import "./ProfilePhotoControl.css";

// Phase H. Self-service profile photo control. Reads the caller's
// current profileImgKey via the `me` query on mount, lets them pick
// a new file (PUT to S3 via presigned URL), then writes the new key
// to socialUsers via the setUserProfileImage mutation. Remove clears.
//
// userId is the socialUsers DDB key, which for new signups matches
// the Cognito sub — UserDashboard hands it in from the parsed token.
// If the user has no socialUsers row yet, the mutation will surface a
// "User not found" error and the upload silently orphans on S3 — the
// row gets created during signup so this is rare in practice.

const ME_QUERY = `
  query MeForProfilePhoto {
    me {
      id
      email
      profileImgKey
    }
  }
`;

const SET_PROFILE_IMAGE = `
  mutation SetMyProfileImage($userId: ID!, $imgKey: String) {
    setUserProfileImage(userId: $userId, imgKey: $imgKey) {
      userId
      profileImgKey
    }
  }
`;

const ProfilePhotoControl = ({ userId, email, name }) => {
  const fileInputRef = useRef(null);
  const [imgKey, setImgKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await executeQuery(ME_QUERY, {});
        if (cancelled) return;
        const key = result?.me?.profileImgKey || null;
        setImgKey(key);
      } catch (err) {
        if (!cancelled) {
          // Non-fatal — the initials avatar still renders.
          // eslint-disable-next-line no-console
          console.warn("Could not load profile image key:", err.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePick = () => {
    if (loading) return;
    fileInputRef.current && fileInputRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // reset so picking the same file twice re-fires
    if (!file) return;
    if (!userId) {
      setError("Can't find your user ID — try signing out and back in.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { s3Key } = await uploadProfilePhoto({ file, targetUserId: userId });
      const result = await executeMutation(SET_PROFILE_IMAGE, {
        userId,
        imgKey: s3Key,
      });
      const next = result?.setUserProfileImage?.profileImgKey || s3Key;
      setImgKey(next);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (loading) return;
    if (!imgKey) return;
    if (!window.confirm("Remove your profile photo?")) return;
    setError("");
    setLoading(true);
    try {
      await executeMutation(SET_PROFILE_IMAGE, { userId, imgKey: null });
      setImgKey(null);
    } catch (err) {
      setError(err.message || "Remove failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-photo-control">
      <UserAvatar
        name={name}
        email={email}
        imgKey={imgKey}
        size={96}
        className="profile-photo-control__avatar"
      />
      <div className="profile-photo-control__actions">
        <button
          type="button"
          className="profile-photo-control__btn profile-photo-control__btn--primary"
          onClick={handlePick}
          disabled={loading}
        >
          {loading ? "Uploading…" : imgKey ? "Replace photo" : "Upload photo"}
        </button>
        {imgKey && (
          <button
            type="button"
            className="profile-photo-control__btn"
            onClick={handleRemove}
            disabled={loading}
          >
            Remove
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          style={{ display: "none" }}
        />
        <p className="profile-photo-control__hint">
          JPEG, PNG, or WEBP — up to 10 MB. Shown next to your name on team
          and event pages.
        </p>
        {error && (
          <p className="profile-photo-control__error">{error}</p>
        )}
      </div>
    </div>
  );
};

export default ProfilePhotoControl;
